import { ClientAvatar } from '@app/components/base/clients/ClientAvatar';
import { Client } from '@app/core/types/client';
import { User, UserRole } from '@mi-front/core';
import {
  Box,
  Chip,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemProps,
  ListItemText,
  Typography,
} from '@mui/material';

interface Props extends ListItemProps {
  readonly user: User & { client: Client };
  readonly button?: boolean;
}

/**
 * List item that shows a user with client avatar, client name, email, optional name/surname, and role chip.
 * Used in impersonation screens for options, current user, and recent users.
 * When button is true, uses ListItemButton for clickable rows.
 */
export function ImpersonationUserListItem(props: Props) {
  const { user, button = false, children, ...listItemProps } = props;
  const ListItemComponent = (
    button ? ListItemButton : ListItem
  ) as React.ComponentType<ListItemProps>;
  const displayName =
    user.name || user.surname ? [user.name, user.surname].filter(Boolean).join(' ') : null;

  return (
    <ListItemComponent {...listItemProps}>
      <ListItemAvatar>
        {user.client && <ClientAvatar client={user.client} size={24} />}
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {user.client?.name}
            {user.role === UserRole.CLIENT_ADMIN && (
              <Chip
                color="info"
                label={user.role.split('_').join(' ')}
                size="small"
                sx={{ typography: 'caption', height: 20 }}
              />
            )}
          </Box>
        }
        secondary={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {user.email}
            {displayName && (
              <Typography variant="caption" color="text.secondary">
                {displayName}
              </Typography>
            )}
          </Box>
        }
        primaryTypographyProps={{
          typography: 'caption',
        }}
        secondaryTypographyProps={{
          component: 'div',
          typography: 'body1',
        }}
      />
      {children}
    </ListItemComponent>
  );
}
